/**
 * OverlayMarkerManager - Manages multiple OverlayMarker instances
 *
 * Provides a clean interface to manage OverlayView markers
 * while maintaining compatibility with existing marker array system
 */

import { createOverlayMarker } from './OverlayMarker.js'

export class OverlayMarkerManager {
  constructor(mapInstance) {
    this.map = mapInstance
    this.markers = new Map() // markerId -> OverlayMarker instance
    this.nextId = 1
  }

  /**
   * Add a new marker to the map
   */
  addMarker(markerData, options = {}) {
    const markerId = options.id || `overlay-marker-${this.nextId++}`

    try {
      // Create OverlayMarker instance using factory function
      const overlayMarker = createOverlayMarker(markerData, {
        onClick: options.onClick || (() => {}),
        tooltipIndex: this.nextId - 1, // Use sequential index for tooltip ID
        ...options,
      })

      // Set the map (this triggers onAdd)
      overlayMarker.setMap(this.map)

      // Store the marker
      this.markers.set(markerId, overlayMarker)

      return markerId
    } catch (error) {
      console.error('Failed to create overlay marker:', error)
      return null
    }
  }

  /**
   * Remove a marker from the map
   */
  removeMarker(markerId) {
    const marker = this.markers.get(markerId)
    if (marker) {
      marker.setMap(null) // This triggers onRemove
      this.markers.delete(markerId)
      return true
    }
    return false
  }

  /**
   * Update marker data
   */
  updateMarker(markerId, newData) {
    const marker = this.markers.get(markerId)
    if (marker) {
      marker.updateMarkerData(newData)
      return true
    }
    return false
  }

  /**
   * Update marker position
   */
  updateMarkerPosition(markerId, position) {
    return this.updateMarker(markerId, { position })
  }

  /**
   * Show/hide marker
   */
  setMarkerVisible(markerId, visible) {
    const marker = this.markers.get(markerId)
    if (marker) {
      marker.setVisible(visible)
      return true
    }
    return false
  }

  /**
   * Set marker z-index
   */
  setMarkerZIndex(markerId, zIndex) {
    const marker = this.markers.get(markerId)
    if (marker) {
      marker.setZIndex(zIndex)
      return true
    }
    return false
  }

  /**
   * Remove all markers
   */
  clearAllMarkers() {
    this.markers.forEach((marker, markerId) => {
      this.removeMarker(markerId)
    })
    this.markers.clear()
    this.nextId = 1
  }

  /**
   * Get marker instance by ID
   */
  getMarker(markerId) {
    return this.markers.get(markerId)
  }

  /**
   * Get all marker IDs
   */
  getAllMarkerIds() {
    return Array.from(this.markers.keys())
  }

  /**
   * Get marker count
   */
  getMarkerCount() {
    return this.markers.size
  }

  /**
   * Check if marker exists
   */
  hasMarker(markerId) {
    return this.markers.has(markerId)
  }

  /**
   * Sync with existing markers array (for backward compatibility)
   * This method allows us to migrate from the old system gradually
   */
  syncWithMarkersArray(markersArray, onClickCallback) {
    // Clear existing overlay markers
    this.clearAllMarkers()

    // Create overlay markers from array
    markersArray.forEach((markerData, index) => {
      this.addMarker(markerData, {
        id: `array-marker-${index}`,
        onClick: onClickCallback,
      })
    })
  }

  /**
   * Convert markers array to overlay markers
   * Useful for migrating from Vue component system
   */
  migrateFromVueMarkers(markersArray, mapInstance, onClickCallback) {
    if (!mapInstance) {
      console.warn('Map instance required for migration')
      return
    }

    // Update map reference if needed
    if (this.map !== mapInstance) {
      this.map = mapInstance
    }

    // Migrate all markers
    this.syncWithMarkersArray(markersArray, onClickCallback)
  }

  /**
   * Force redraw all markers
   */
  redrawAllMarkers() {
    this.markers.forEach(marker => {
      marker.draw()
    })
  }

  /**
   * Cleanup resources
   */
  destroy() {
    this.clearAllMarkers()
    this.map = null
  }
}
